import { AcceptDocumentsModalInnerProps } from "@app/features/accept-documents/ui/AcceptDocumentsModal.tsx";
import {
	FxIdSdkAdapterSocialSettingDefault,
	FxIdSdkAdapterStickyBannerShowErrorReason,
	IExportedFxIdSdkMethod,
	IFxIdAddToFavoritesRequest,
	IFxIdAddToFavoritesResult,
	IFxIdJoinCommunityRequest,
	IFxIdJoinCommunityResult,
	IFxIdSdkAdapterAuthenticateUserRequest,
	IFxIdSdkAdapterAuthenticateUserResponse,
	IFxIdSdkAdapterBuyProductRequest,
	IFxIdSdkAdapterBuyProductResponse,
	IFxIdSdkAdapterCanReviewResult,
	IFxIdSdkAdapterGetFriendsRequest,
	IFxIdSdkAdapterInfoSocial,
	IFxIdSdkAdapterInviteFriendsRequest,
	IFxIdSdkAdapterOpenModalRequestDto,
	IFxIdSdkAdapterOpenModalResultDto,
	IFxIdSdkAdapterRequestReviewResult,
	IFxIdSdkAdapterSocialSettings,
	IFxIdSdkAdapterStatEventRequest,
	IFxIdSdkAdapterStatInitializeRequest,
	IFxIdSdkAdapterStickyBannerShowResultDto,
	IFxIdSdkGetFriendsResult,
	IFxIdSdkInviteFriendsResult,
	IFxIdSdkRateApplicationRequest,
	IFxIdSdkRateApplicationResult,
	IFxIdSdkRatingResponse,
	IFxIdSdkWordsFilterRequest,
	IFxIdSdkWordsFilterResult
} from "@app/sdk/FxIdSdkTypes.ts";
import {
	FxIdDomainEntitiesDocumentsDocument,
	FxIdDomainStoreEnumsSupportedWebPublishingPlatform,
	FxIdWebFeaturesGamesChooseGameResponsePublicGameData
} from "@app/shared/api/gen";
import OpenApiClient from "@app/shared/api/OpenApiClient.ts";
import * as Analytics from "@app/shared/lib/Analytics";
import { getDocUrl } from "@app/shared/lib/Functions";
import { exitFullscreen } from "@app/shared/lib/Utils.ts";
import { openContextModal } from "@mantine/modals";
import { IframeType, IframeTypeToSupportedPlatformMap } from "@sdk/constants.ts";
import { openIframeModal } from "@shared/ui/Modal/OpenIframeModal.tsx";
import i18next from "i18next";
import { findAdapterConfig } from "src/config";

export abstract class FxIdSdkBaseAdapter {
	abstract readonly adapter: IframeType;
	readonly defaultSocialSettings: IFxIdSdkAdapterSocialSettings = FxIdSdkAdapterSocialSettingDefault;

	protected constructor(
		protected exportedSdk: IExportedFxIdSdkMethod,
		protected game: string,
		protected config: FxIdWebFeaturesGamesChooseGameResponsePublicGameData
	) {}

	abstract Initialize(): Promise<void>;

	abstract GetSocialInfo(): Promise<IFxIdSdkAdapterInfoSocial>;

	abstract BuyProduct(request: IFxIdSdkAdapterBuyProductRequest): Promise<IFxIdSdkAdapterBuyProductResponse>;

	async SocialSettings(): Promise<IFxIdSdkAdapterSocialSettings> {
		const adapterSettings = findAdapterConfig(this.config.PublicWebClientConfig.Adapters, this.adapter);
		const adapterUiDisabled = adapterSettings?.DisableHeaderUi === true;

		const settings: IFxIdSdkAdapterSocialSettings = {
			...this.defaultSocialSettings
		};

		if (adapterUiDisabled) {
			settings.ui.disabled = true;
		}

		return Promise.resolve(settings);
	}

	abstract RegisterShareHandlers(): Promise<void>;

	abstract StoreCurrency(): Promise<string | undefined>;

	abstract StatInitialize(request: IFxIdSdkAdapterStatInitializeRequest): Promise<void>;

	abstract StatEvent(request: IFxIdSdkAdapterStatEventRequest): Promise<void>;

	CanReview(): Promise<IFxIdSdkAdapterCanReviewResult> {
		return Promise.resolve({ canReview: false });
	}

	RequestReview(): Promise<IFxIdSdkAdapterRequestReviewResult> {
		return Promise.resolve({ shown: false });
	}

	ReviewInfo(): Promise<IFxIdSdkRatingResponse> {
		return Promise.resolve({ isRatingAvailable: false });
	}

	StoreAdditionalData(): Promise<unknown | undefined> {
		return Promise.resolve(undefined);
	}

	AdsIsVideoReady(): Promise<boolean> {
		// ignore
		return Promise.resolve(false);
	}

	AdsShowVideo(): Promise<void> {
		// ignore
		return Promise.resolve();
	}

	AdsStickyBannerShow(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	AdsIsInterstitialReady(): Promise<boolean> {
		// ignore
		return Promise.resolve(false);
	}

	AdsShowInterstitial(): Promise<void> {
		// ignore
		return Promise.resolve();
	}

	AdsStickyBannerHide(): Promise<IFxIdSdkAdapterStickyBannerShowResultDto> {
		return Promise.resolve({
			success: false,
			errorReason: FxIdSdkAdapterStickyBannerShowErrorReason.NotImplemented
		});
	}

	GetFriends(_request: IFxIdSdkAdapterGetFriendsRequest): Promise<IFxIdSdkGetFriendsResult> {
		return Promise.resolve({ friends: [] });
	}

	InviteFriends(_request: IFxIdSdkAdapterInviteFriendsRequest): Promise<IFxIdSdkInviteFriendsResult> {
		return Promise.resolve({ friends: [] });
	}

	AuthenticateUser(
		_request: IFxIdSdkAdapterAuthenticateUserRequest
	): Promise<IFxIdSdkAdapterAuthenticateUserResponse> {
		return Promise.resolve({});
	}

	JoinCommunity(_request: IFxIdJoinCommunityRequest): Promise<IFxIdJoinCommunityResult> {
		return Promise.resolve({ success: false });
	}

	AddToFavorites(_request: IFxIdAddToFavoritesRequest): Promise<IFxIdAddToFavoritesResult> {
		return Promise.resolve({ success: false });
	}

	WordsFilter(request: IFxIdSdkWordsFilterRequest): Promise<IFxIdSdkWordsFilterResult> {
		return Promise.resolve({ success: true });
	}

	RateApplication(request: IFxIdSdkRateApplicationRequest): Promise<IFxIdSdkRateApplicationResult> {
		return Promise.resolve({ success: true });
	}

	IsFocusChanged() {
		const eventHandler = () => {
			// Проверяем, скрыта ли страница
			if (document.hidden) {
				this.exportedSdk.DispatchFocusChanged({ focus: false });
			} else {
				this.exportedSdk.DispatchFocusChanged({ focus: true });
			}
		};
		document.addEventListener("visibilitychange", eventHandler);
	}

	async LoadingReady(): Promise<void> {
		log.info("Called LoadingReady api");
		return Promise.resolve();
	}

	async GameStarted(): Promise<void> {
		log.info("Called GameStarted api");
		return Promise.resolve();
	}

	async GameStopped(): Promise<void> {
		log.info("Called GameStopped api");
		return Promise.resolve();
	}

	/**
	 * Перенаправляет некоторые URL-адреса на внутренние ресурсы или специальные страницы.
	 * Это временное решение для обработки специфичных ссылок.
	 * @param url - исходный URL для возможного перенаправления.
	 * @returns измененный или оригинальный URL.
	 */
	protected async __HACK_RemapUrl(url: URL): Promise<URL> {
		if (import.meta.env.VITE_APP_TENANT_OVERRIDE === "" || import.meta.env.VITE_APP_TENANT_OVERRIDE === "FX") {
			if (
				url.toString().startsWith("https://fxgames.zendesk.com/hc") ||
				url.toString().match(/\/docs\/(.*)\/faq.html/)
			) {
				// log.info("Remapping to freshdesk");
				// return new URL("https://fxgames.freshdesk.com/support/solutions");
				log.info("Remapping to faq.txt");
				const docUrl = await getDocUrl({
					docFile: "faq.html",
					adapter: this.adapter
				});
				return docUrl;
			}
			if (url.toString().startsWith("https://discord")) {
				log.info("Remapping discrod url");
				const url = new URL(`${import.meta.env.BASE_URL}/discord-qr`, window.location.origin);
				url.searchParams.append("game", this.game);
				return url;
			}
			if (url.toString().startsWith("https://fx.gl/terms-of-service")) {
				log.info("Remapping tos url");
				const docUrl = await getDocUrl({
					docFile: "tos.txt",
					adapter: this.adapter
				});
				return docUrl;
			}
			if (url.toString().startsWith("https://fx.gl/privacy-policy")) {
				log.info("Remapping pp url");
				const docUrl = await getDocUrl({
					docFile: "pp.txt",
					adapter: this.adapter
				});
				return docUrl;
			}
		}
		return url;
	}

	async OpenModal(requestJson: string): Promise<IFxIdSdkAdapterOpenModalResultDto> {
		log.info("Opening modal %o", requestJson);

		const request: IFxIdSdkAdapterOpenModalRequestDto = JSON.parse(requestJson);
		const url = new URL(request.Url);

		if (
			this.game === "gc" &&
			(url.href.toLowerCase().includes("tos") ||
				url.href.toLowerCase().includes("pp") ||
				url.href.toLowerCase().includes("terms-of-service") ||
				url.href.toLowerCase().includes("privacy-policy"))
		) {
			try {
				const allDocumentsResponse =
					await OpenApiClient.Games.fxIdWebFeaturesDocumentsGetAllDocumentsWithVersionsGetAllDocumentsWithVersionsEndpoint(
						{
							GameSystemName: this.game
						}
					);

				let documentToShow: FxIdDomainEntitiesDocumentsDocument | undefined;
				const lowerCaseUrl = url.href.toLowerCase();

				if (lowerCaseUrl.includes("tos") || lowerCaseUrl.includes("terms-of-service")) {
					documentToShow = allDocumentsResponse.Documents.find((doc) => doc.DocumentType.Value === "TOS");
				} else if (lowerCaseUrl.includes("pp") || lowerCaseUrl.includes("privacy-policy")) {
					documentToShow = allDocumentsResponse.Documents.find((doc) => doc.DocumentType.Value === "PP");
				}

				if (documentToShow && documentToShow.Versions.length > 0) {
					await exitFullscreen();
					return await new Promise((resolve) => {
						openContextModal({
							modal: "acceptDocuments",
							title: i18next.t("accept_documents.title", "Accept document"),
							innerProps: {
								documentToAccept: documentToShow,
								latestVersion: documentToShow.Versions[0]
							} as AcceptDocumentsModalInnerProps,
							styles: {
								close: {
									backgroundColor: "transparent",
									color: "var(--mantine-color-white)",
									outline: "none"
								},
								content: {
									backgroundColor: "var(--mantine-color-white)",
									color: "var(--mantine-color-black)"
								},

								// Стиль для тела модального окна
								body: {
									height: "100%",
									display: "flex",
									flexDirection: "column",
									padding: 0
								}
							},
							closeOnClickOutside: true,
							closeOnEscape: true,
							withCloseButton: true,
							onClose: () => {
								log.info("Document acceptance modal closed, resolving promise.");
								resolve({ Success: true });
							}
						});
					});
				}
			} catch (error) {
				log.error("Failed to fetch or show document:", error);
				// Fallback to default behavior in case of an error
			}
		}

		await exitFullscreen();

		const remappedUrl = await this.__HACK_RemapUrl(url);

		if (this.adapter !== "fxid") {
			const platform = IframeTypeToSupportedPlatformMap[this.adapter];
			remappedUrl.searchParams.append("social", FxIdDomainStoreEnumsSupportedWebPublishingPlatform[platform]);
		}

		openIframeModal({ url: remappedUrl.toString() });
		void Analytics.sdk.openIframeModal({
			game: this.game,
			adapter: this.adapter,
			url: remappedUrl.toString()
		});

		return Promise.resolve({ Success: true });
	}
}
